import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/svgpathbuilde.css';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

interface Step {
  id: number;
  title: string;
  description: string;
  position: number;
  icon: string;
  activeicon:string;
  cardLeft?: number;
  cardTop?: number;
}

interface PathProgressScrollerProps {
  steps: Step[];
  svgPath: string;
  svgViewBox: string;
  className?: string;
}

const PathProgressScroller: React.FC<PathProgressScrollerProps> = ({
  steps,
  svgPath,
  svgViewBox,
  className = '',
}) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [pathLength, setPathLength] = useState(0);
  const [dotPosition, setDotPosition] = useState({ x: 0, y: 0 });
  const [activeSteps, setActiveSteps] = useState<Set<number>>(new Set());

  const containerRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null);

  // Calculate path length on mount
  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength();
      setPathLength(length);
    }
  }, [svgPath]);

  // Get point along path
  const getPointAtLength = useCallback((length: number) => {
    if (pathRef.current) {
      return pathRef.current.getPointAtLength(length);
    }
    return { x: 0, y: 0 };
  }, []);

  // Update dot position and active steps
  useEffect(() => {
    if (pathLength > 0) {
      const currentLength = scrollProgress * pathLength;
      const point = getPointAtLength(currentLength);
      setDotPosition(point);

      const newActiveSteps = new Set<number>();
      steps.forEach(step => {
        if (scrollProgress >= step.position) {
          newActiveSteps.add(step.id);
        }
      });
      setActiveSteps(newActiveSteps);
    }
  }, [scrollProgress, pathLength, getPointAtLength, steps]);

  // Setup GSAP ScrollTrigger
  useEffect(() => {
    if (!containerRef.current || pathLength === 0) return;

    // Create ScrollTrigger
    scrollTriggerRef.current = ScrollTrigger.create({
      trigger: containerRef.current,
      start: "center center",
      end: "+=300vh",
      pin: true,
      scrub: 1,
      onUpdate: (self) => {
        // Update progress based on scroll position
        const progress = self.progress;
        setScrollProgress(progress);
      },
      onRefresh: () => {
        // Refresh when window resizes
        if (pathRef.current) {
          const length = pathRef.current.getTotalLength();
          setPathLength(length);
        }
      }
    });

    // Cleanup
    return () => {
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
      }
    };
  }, [pathLength]);

  // Refresh ScrollTrigger on window resize
  useEffect(() => {
    const handleResize = () => {
      ScrollTrigger.refresh();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div ref={containerRef} className='container-new'>
      {/* Heading */}
      <div className="heading-new">
        <p className='title-new'>Our Process</p>
      </div>

      <div className="svgContainer-new">
        <div className="svgWrapper-new">
          <svg 
            viewBox={svgViewBox} 
            className="svg-new"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id="progress-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#A749A3" />
                <stop offset="100%" stopColor="#3844D2" />
              </linearGradient>
            </defs>

            {/* Background Path */}
            <path
              d={svgPath}
              stroke="#000000"
              strokeWidth="0.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="10, 5"
            />

            {/* Progress Path */}
            <path
              ref={pathRef}
              d={svgPath}
              stroke="url(#progress-gradient)"
              strokeWidth="1"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={pathLength}
              strokeDashoffset={pathLength * (1 - scrollProgress)}
              className="transition-all duration-100 ease-out"
            />

            {/* Step Icons */}
            {steps.map((step) => {
              const stepPoint = getPointAtLength(step.position * pathLength);
              const isActive = activeSteps.has(step.id);
              const isCurrentActive = Math.abs(scrollProgress - step.position) < 0.05;

              return (
                <g key={step.id}>
                  <image
                    href={isActive ? step.activeicon : step.icon}
                    x={stepPoint.x - (isCurrentActive ? 25 : 25)}
                    y={stepPoint.y - (isCurrentActive ? 25 : 25)}
                    width={isCurrentActive ? "50" : "50"}
                    height={isCurrentActive ? "50" : "50"}
                    className="transition-all duration-300"
                  />
                </g>
              );
            })}      

            {/* Moving Dot */}
            <circle
              cx={0}
              cy={0}
              r="8"
              fill="url(#progress-gradient)"
              transform={`translate(${dotPosition.x}, ${dotPosition.y})`}
              className="transition-transform duration-300 ease-out"
            />
          </svg>

          {/* Step Content Cards */}
          {steps.map((step) => {
            const isActive = activeSteps.has(step.id);
            if (!isActive) return null;

            const stepPoint = getPointAtLength(step.position * pathLength);

            const svgRect = { width: 1064, height: 280 };
            const leftPercent = (stepPoint.x / svgRect.width) * 100;
            const topPercent = (stepPoint.y / svgRect.height) * 100;
            const adjustedLeft = step.cardLeft ?? leftPercent;
            const adjustedTop = step.cardTop ?? topPercent;

            return (
              <div
                key={step.id}
                className="absolute transition-all duration-500 opacity-100 scale-100 translate-y-0"
                style={{
                  left: `${adjustedLeft}%`,
                  top: `${adjustedTop}%`,
                }}
              >
                <div className="card-new">
                  <p className="content-f-heading-new">{step.title}</p>
                  <p className="content-f-des-new">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PathProgressScroller;